import api from '@/lib/api';
import type {SshKeyDto} from '@/api/hetzner';
import type {MonthlyPrice} from '@/api/types';
import type {CloudProvider} from '@/lib/providers';

export type {MonthlyPrice} from '@/api/types';

export type RunnerPlatform = 'GITLAB' | 'GITHUB';

/** Where a GitHub runner is registered; the token is never returned. */
export interface GithubRunnerScope {
  owner: string;
  /** Null for an organization runner. */
  repository: string | null;
  /** Organization runners only; null means the default group. */
  runnerGroup: string | null;
}

export interface RunnerResponse {
  runnerId: string;
  productId: string;
  name: string;
  cpus: number;
  memory: number;
  disk: number;
  concurrency: number;
  /** Null for a GitHub runner. */
  executor: string | null;
  ipv4: string;
  ipv6: string;
  state: string;
  stateDescription: string;
  /**
   * False when Hetzner reports this server type as unavailable in the runner's location.
   * Used to disable Duplicate/Resume and warn before Pause.
   */
  duplicatable: boolean;
  locationCountry: string;
  locationCity: string;
  /** Null for a GitHub runner. */
  gitlabHost: string | null;
  cpuArchitecture: string;
  sshKeyIds: string[];
  labels: string[];
  /**
   * Current Hetzner Cloud net monthly list price for this runner's product + datacenter.
   * Null/undefined when pricing is unavailable.
   */
  monthlyPrice?: MonthlyPrice | null;
  monitoringEnabled: boolean;
  /** The Metrics connection this runner uses; omitted without one. */
  metricsConnectionId?: string;
  metricsConnectionName?: string;
  /** The collector's last reported Metrics result for the current VM; omitted without Metrics or a report. */
  metricsStatus?: { status: MetricsStatus; reportedAt: string } | null;
  /** Present once the watchdog was ever enabled. */
  watchdog?: RunnerWatchdog | null;
  /** The cache this runner uses, if any. */
  cacheId?: string | null;
  cacheName?: string | null;
  /** Missing means GitLab. */
  platform?: RunnerPlatform;
  /** GitHub runners only. */
  github?: GithubRunnerScope | null;
  /** Only for a burst runner, which its anchor's scaling policy created and which can only be deleted. */
  burst?: BurstReference;
  /** Missing means Hetzner. */
  provider?: CloudProvider;
}

/** Restarts the runner by a forced pause and resume when its collector stops reporting. */
export interface RunnerWatchdog {
  enabled: boolean;
  timeoutMinutes: number;
  /** Cumulative. */
  restarts: number;
  lastRestartAt: string | null;
  /** RESUME_FAILED: the runner could not be resumed and is not retried; its state shows whether it is paused. */
  lastRestartResult: 'RESUMED' | 'RESUME_FAILED' | null;
  /** RESTART_LIMIT: switched off after too many restarts within 24 hours. */
  disabledReason: 'RESTART_LIMIT' | null;
}

export interface BurstReference {
  policyId: string;
  anchorRunnerId: string;
  /** MANAGED (`/rest/runner`) or EXTERNAL (`/rest/external-runner`). */
  anchorKind: 'MANAGED' | 'EXTERNAL';
  /** Null once the anchor is gone. */
  anchorName: string | null;
  /** When it was reserved, before its server was requested. */
  created?: string;
  /** When Hetzner created its server, from which it is billed; null without a server. */
  startedAt?: string | null;
  /** Being removed, or unknown, like the LEAVING phase of its policy's status. */
  leaving?: boolean;
}

export interface RunnerDeletedResponse {
  runnerId: string;
}

export async function listRunners() {
  const { data } = await api.get<RunnerResponse[]>('runner');
  return data;
}

export async function getRunner(runnerId: string | number) {
  const { data } = await api.get<RunnerResponse>(`runner/${runnerId}`);
  return data;
}

/**
 * Labels already used on existing runners, offered as suggestions in the labels field.
 * Uses the existing user-scoped runner list endpoint; no dedicated backend endpoint needed.
 *
 * @param excludeRunnerId Runner to exclude (e.g. the runner currently being edited), so labels
 *                        used only by that runner are not suggested back to the user.
 * @returns Flattened, trimmed, unique (case-sensitive), alphabetically sorted labels.
 */
export async function listExistingRunnerLabels(
  excludeRunnerId?: string | number
): Promise<string[]> {
  const runners = await listRunners();
  const excluded = excludeRunnerId != null ? String(excludeRunnerId) : null;
  const unique = new Set<string>();
  for (const runner of runners) {
    if (excluded !== null && String(runner.runnerId) === excluded) continue;
    for (const label of runner.labels ?? []) {
      const trimmed = String(label ?? '').trim();
      if (trimmed) unique.add(trimmed);
    }
  }
  // Deterministic alphabetical order: case-insensitive first, exact match as tiebreak.
  return [...unique].sort((a, b) => {
    const lowerA = a.toLowerCase();
    const lowerB = b.toLowerCase();
    if (lowerA < lowerB) return -1;
    if (lowerA > lowerB) return 1;
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  });
}

/**
 * Without `force`, a runner drains: it takes no new jobs, and its server is deleted once the running jobs
 * have finished, after at most an hour. Resolves to the state the runner is in afterwards, PAUSING or PAUSED.
 */
export async function pauseRunner(
  runnerId: string | number,
  options: { force?: boolean } = {}
): Promise<string> {
  const { data } = await api.post<{ runnerId: string; state: string }>(
    `runner/${runnerId}/pause`,
    undefined,
    options.force ? { params: { force: true } } : undefined
  );
  return data.state;
}

export async function resumeRunner(runnerId: string | number) {
  await api.post<void>(`runner/${runnerId}/resume`);
}

export async function duplicateRunner(runnerId: string | number) {
  const { data } = await api.post<{ runnerId: string }>(`runner/${runnerId}/duplicate`);
  return data;
}

export async function deleteRunner(runnerId: string | number) {
  const { data } = await api.delete<RunnerDeletedResponse>(`runner/${runnerId}`);
  return data;
}
export async function forceDeleteRunner(runnerId: string | number) {
  const { data } = await api.delete<RunnerDeletedResponse>(`runner/${runnerId}/unknown`);
  return data;
}

export interface CreateRunnerRequest {
  /** Omitted: the backend picks a name such as swift-falcon. */
  name?: string;
  productId: string;
  /** Omitted for GitLab runners. */
  platform?: RunnerPlatform;
  /** GitLab runners only, like runnerToken and executor. */
  gitlabHost?: string;
  runnerToken?: string;
  /** GitHub runners only: repository for a repository runner, runnerGroup for an organization runner. */
  github?: { owner: string; repository?: string; runnerGroup?: string; token: string };
  /** Omitted means Hetzner. */
  provider?: CloudProvider;
  /** One of the two tokens is required; `providerAuthToken` takes precedence. */
  hetznerAuthToken?: string;
  providerAuthToken?: string;
  concurrency: number;
  /** The provider's location code. */
  location: string;
  executor?: string;
  sshKeyIds: string[];
  labels: string[];
  monitoringEnabled?: boolean;
  metricsConnectionId?: string;
  /** GitLab runners only. */
  cacheId?: string;
  /** Needs Monitoring or Metrics; omitted means off. */
  watchdogEnabled?: boolean;
  /** 5 to 60, default 10. */
  watchdogTimeoutMinutes?: number;
}

export interface UpdateRunnerRequest {
  name: string;
  productId: string;
  /** GitLab runners only, like runnerToken and executor; omitted keeps the current value. */
  gitlabHost?: string;
  runnerToken?: string;
  /** A new token of the runner's provider; Hetzner runners send `hetznerAuthToken`. */
  hetznerAuthToken?: string;
  providerAuthToken?: string;
  concurrency: number;
  executor?: string;
  /** GitHub runners only: a new token, checked with GitHub and stored without recreating the VM. */
  github?: { token: string };
  sshKeyIds: string[];
  labels: string[];
  /** Omit to keep the current preference. */
  monitoringEnabled?: boolean;
  /** Omit to keep the current connection; an empty string switches Metrics off. Changing it recreates the VM. */
  metricsConnectionId?: string;
  /** Omit to keep the current cache; an empty string removes it. Changing it recreates the VM. */
  cacheId?: string;
  /** Omit to keep the current setting; changing the watchdog never recreates the VM. */
  watchdogEnabled?: boolean;
  watchdogTimeoutMinutes?: number;
}

export interface UpdateRunnerResponse {
  runnerId: string;
}

/** Messages for the runner API's refusals of GitHub settings and lifecycle conflicts. */
const RUNNER_ERROR_MESSAGES: Record<string, string> = {
  GITHUB_TOKEN_INVALID: 'GitHub rejected the token as invalid, expired or revoked.',
  GITHUB_PERMISSION_DENIED: 'The GitHub token lacks the required permission.',
  GITHUB_NOT_FOUND:
    'The repository or organization was not found, or the GitHub token cannot access it.',
  GITHUB_UNAVAILABLE:
    'GitHub could not be reached or limited requests. Try again in a few minutes.',
  INVALID_CONCURRENCY: 'GitHub runners run 1 to 32 jobs at a time.',
  INVALID_LABELS: 'These labels are not valid for GitHub runners.',
  CACHE_NOT_SUPPORTED: 'Caches are available only for GitLab runners.',
  PLATFORM_MISMATCH: "These settings belong to another CI platform than the runner's.",
  RUNNER_STATE_CONFLICT: 'The runner changed in the meantime, so nothing was changed.',
  INVALID_WATCHDOG: 'The watchdog needs Monitoring or Metrics and a timeout of 5 to 60 minutes.',
  RUNNER_PAUSED_TOKEN_REFUSED:
    'The new settings were saved and the runner is paused, because GitHub refused its token. ' +
    'Replace the GitHub token, then resume the runner.',
};

// The backend's detail names the missing permission, the scope, the label or watchdog rules, and never contains the token.
const DETAILED_CODES = new Set([
  'GITHUB_PERMISSION_DENIED',
  'GITHUB_NOT_FOUND',
  'INVALID_LABELS',
  'INVALID_WATCHDOG',
  'RUNNER_PAUSED_TOKEN_REFUSED',
]);

export interface RunnerProblem {
  status?: number;
  code?: string;
  message: string;
}

/** The status, code and user message of a failed runner request; [fallback] covers responses without a text. */
export function runnerProblem(error: unknown, fallback: string): RunnerProblem {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response;
  const body = (response?.data ?? {}) as { code?: unknown; detail?: unknown; message?: unknown };
  const code = typeof body.code === 'string' ? body.code : undefined;
  const detail = typeof body.detail === 'string' && body.detail ? body.detail : undefined;
  const known = code ? RUNNER_ERROR_MESSAGES[code] : undefined;
  const message = known
    ? (DETAILED_CODES.has(code!) && detail) || known
    : detail || (typeof body.message === 'string' && body.message) || fallback;
  return { status: response?.status, code, message };
}

export async function updateRunner(runnerId: string | number, payload: UpdateRunnerRequest) {
  const { data } = await api.put<UpdateRunnerResponse>(`runner/${runnerId}`, payload);
  return data;
}

export type MonitoringRange = '1h' | '24h' | '7d' | '30d' | '90d';

export interface MonitoringDisk {
  id: string;
  device: string | null;
  mount: string | null;
  total: number | null;
  used: number | null;
  available: number | null;
}

export interface MonitoringLatest {
  receivedAt: string;
  cpu: number | null;
  memoryUsed: number | null;
  memoryTotal: number | null;
  swapUsed?: number | null;
  swapTotal?: number | null;
  jobsRunning?: number | null;
  disks: MonitoringDisk[] | null;
}

export interface MonitoringBucketDisk {
  id: string;
  device: string | null;
  mount: string | null;
  count: number;
  total: number | null;
  usedAverage: number | null;
}

export interface MonitoringBucket {
  at: string;
  generation: number;
  count: number;
  cpuAverage: number | null;
  memoryAverage: number | null;
  swapAverage?: number | null;
  jobsAverage?: number | null;
  jobsPeak?: number | null;
  disks: MonitoringBucketDisk[] | null;
}

export type MetricsStatus =
  'ok' | 'unreachable' | 'tls' | 'auth' | 'rejected' | 'server' | 'timeout' | 'internal';

export interface RunnerMonitoringEntry {
  runnerId: string;
  latest: MonitoringLatest | null;
  history: MonitoringBucket[] | null;
  historyError: string | null;
}

export interface RunnerMonitoringBatch {
  range: MonitoringRange;
  from: string;
  to: string;
  bucketSeconds: number;
  /** Ranges justified by retained history, independent of `range`; empty without data. */
  availableRanges?: string[];
  runners: RunnerMonitoringEntry[];
}

/**
 * Metrics for all of the caller's active runners with monitoring enabled.
 * Rejects with HTTP 403 while the subscription is not active.
 */
export async function getRunnersMonitoring(range: MonitoringRange) {
  const { data } = await api.get<RunnerMonitoringBatch>('runner/monitoring', {
    params: { range },
  });
  return data;
}

/**
 * Fetch available SSH keys for a runner. These include all keys that can be assigned
 * to the runner; keys already configured on the runner should be preselected in the UI.
 * Backend endpoint: GET /rest/runner/{runnerId}/availablesshkeys
 */
export async function getRunnerAvailableSshKeys(runnerId: string | number): Promise<SshKeyDto[]> {
  const { data } = await api.get<SshKeyDto[]>(`runner/${runnerId}/availablesshkeys`);
  return Array.isArray(data) ? data : [];
}
